import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CheckIcon from "@material-ui/icons/CheckRounded";
import { toast } from "react-toastify";

import api from "../../services/api";
import CheckoutModal from "./CheckoutModal";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import numeraMark from "../../assets/logo-numera-mark.svg";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg || "#F6F7F9" },
  wrap: { width: "100%", maxWidth: 1120, margin: "0 auto", padding: theme.spacing(7, 3, 9) },

  // cabeçalho centralizado, com respiro proposital (sem void no topo)
  head: { textAlign: "center", marginBottom: theme.spacing(5.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 800, fontSize: 30, letterSpacing: "-0.01em", color: theme.app.ink },
  subtitle: { color: theme.app.textSecondary, fontSize: 14.5, lineHeight: 1.55, marginTop: 10, maxWidth: 580, marginLeft: "auto", marginRight: "auto" },

  // grid 4 col, alturas iguais (stretch) → CTA ancora no rodapé de todos
  grid: {
    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18, alignItems: "stretch",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, 1fr)" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },

  card: {
    background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 18,
    padding: "26px 22px 24px", display: "flex", flexDirection: "column", position: "relative",
  },
  // ⭐ card recomendado ESCURO — onde a marca vive (charcoal + verde acid)
  cardRec: {
    background: "linear-gradient(165deg, #333D46 0%, #20272E 100%)",
    border: "1px solid #333D46", color: "#fff",
    transform: "translateY(-10px)", padding: "34px 24px 28px", overflow: "hidden",
    boxShadow: "0 24px 48px -12px rgba(20,26,32,.45)",
    [theme.breakpoints.down("xs")]: { transform: "none" },
  },
  cardCurrent: { borderColor: theme.app.ok || "#3f7a1e", boxShadow: `0 0 0 1px ${theme.app.ok || "#3f7a1e"}` },

  // marca d'água (só no card escuro), bem sutil no canto
  mark: {
    position: "absolute", right: -28, bottom: -24, width: 168, height: 168, opacity: 0.06,
    filter: "brightness(0) invert(1)", pointerEvents: "none", userSelect: "none",
  },

  badge: {
    display: "inline-block", alignSelf: "flex-start", fontSize: 10.5, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.06em", borderRadius: 999, padding: "3px 10px", marginBottom: 14,
  },
  badgeRec: { background: theme.app.lime || "#CEFF00", color: "#1a1f24" },
  badgeCurrent: { background: "#EAF6E0", color: "#3f7a1e" },
  badgeSpacer: { height: 30 }, // reserva a altura do selo p/ alinhar cards sem selo

  name: { fontSize: 13.5, fontWeight: 600, color: theme.app.textSecondary },
  nameRec: { color: "#c3ccd4" },
  price: { fontFamily: theme.app.heading, fontWeight: 800, fontSize: 30, lineHeight: 1.1, letterSpacing: "-0.02em", color: theme.app.ink, marginTop: 4 },
  priceRec: { color: "#fff" },
  priceUnit: { fontSize: 13, fontWeight: 500, color: theme.app.textTertiary },
  priceUnitRec: { color: "#8b95a0" },
  forWho: { fontSize: 12.5, color: theme.app.textTertiary, lineHeight: 1.5, marginTop: 8, minHeight: 36 },
  forWhoRec: { color: "#8b95a0" },

  divider: { height: 1, background: theme.app.lineSoft || "#EEF0F3", margin: "18px 0 0" },
  dividerRec: { background: "rgba(255,255,255,.1)" },

  feats: { listStyle: "none", padding: 0, margin: "16px 0 20px", display: "flex", flexDirection: "column", gap: 10, flex: 1 },
  feat: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: theme.app.textTertiary, "& b": { color: theme.app.ink, fontWeight: 600 }, "& svg": { fontSize: 15, color: theme.app.ok || "#1E9E62", flexShrink: 0, marginTop: 1 } },
  featRec: { color: "#8b95a0", "& b": { color: "#fff" }, "& svg": { color: theme.app.lime || "#CEFF00" } },

  cta: { textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: 10, padding: "11px 16px", boxShadow: "none" },
  ctaAcid: { background: theme.app.lime || "#CEFF00", color: "#1a1f24", "&:hover": { background: theme.app.limeHover || "#bce600", boxShadow: "none" } },
  ctaOutline: { background: "transparent", border: `1px solid ${theme.app.borderInput || "#D8DBDF"}`, color: theme.app.ink, "&:hover": { background: theme.app.hover || "#FAFBFC", borderColor: theme.app.borderInput || "#D8DBDF" } },
  ctaCurrent: { background: theme.app.lineSoft || "#EEF0F3", color: theme.app.textTertiary, "&:hover": { background: theme.app.lineSoft || "#EEF0F3" } },
  ctaCurrentRec: { background: "rgba(255,255,255,.08)", color: "#8b95a0", "&:hover": { background: "rgba(255,255,255,.08)" } },
}));

const showUnit = cents => cents != null; // /mês em Grátis e pagos, não em "Sob consulta"
const priceLabel = cents => (cents == null ? "Sob consulta" : `R$ ${(cents / 100).toFixed(0)}`);
const fmtLimit = v => (v == null ? "Ilimitado" : v.toLocaleString("pt-BR"));
const fmtStorage = mb => (mb == null ? "Ilimitado" : mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`);

// "para quem é" — diferencia sem obrigar a comparar número a número
const AUDIENCE = {
  free: "Para experimentar e projetos pessoais.",
  starter: "Para quem está começando a escalar.",
  pro: "Para operações que rodam de verdade.",
  business: "Para times e alto volume, com suporte.",
};

// 7 features IDÊNTICAS em ordem e quantidade nos 4 cards → dá pra cruzar coluna e linha.
// DID não entra: é comprado na BR DID em qualquer plano.
const featureRows = limits => [
  { label: "Fluxos ativos", val: fmtLimit(limits.activeFlows) },
  { label: "Execuções/mês", val: fmtLimit(limits.flowExecutionsPerMonth) },
  { label: "Templates WhatsApp", val: fmtLimit(limits.whatsappTemplates) },
  { label: "SMS/mês", val: fmtLimit(limits.smsPerMonth) },
  { label: "Conectores", val: fmtLimit(limits.connectors) },
  { label: "Armazenamento", val: fmtStorage(limits.storageMb) },
  { label: "Usuários", val: fmtLimit(limits.users) },
];

const Plans = () => {
  const classes = useStyles();
  const { confirm } = useConfirm();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null); // code do plano atual
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null); // plano em contratação

  const load = useCallback(async () => {
    try {
      const [pl, me] = await Promise.all([
        api.get("/plans"),
        api.get("/me/plan").catch(() => ({ data: null })),
      ]);
      setPlans((pl.data && pl.data.plans) || []);
      setCurrent(me.data && me.data.plan && me.data.plan.code);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subscribe = async p => {
    if (busy) return;
    // plano pago abre o checkout (cartão ou PIX); o gratuito é troca direta
    if (p.monthlyPriceCents) {
      setCheckout(p);
      return;
    }
    const ok = await confirm({
      title: `Mudar para ${p.name}`,
      message: `Trocar seu plano para ${p.name}? Os limites passam a valer na hora.`,
      confirmText: "Mudar de plano",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/me/subscribe", { plan: p.code });
      toast.success(`Plano alterado para ${p.name}.`);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const renderCta = (p, isCurrent, isBusiness, rec) => {
    if (isCurrent) {
      return <Button disabled className={`${classes.cta} ${rec ? classes.ctaCurrentRec : classes.ctaCurrent}`}>Plano atual</Button>;
    }
    if (isBusiness) {
      return <Button className={`${classes.cta} ${classes.ctaOutline}`} href="mailto:vendas@numera.io?subject=Plano Business">Falar com vendas</Button>;
    }
    return (
      <Button className={`${classes.cta} ${rec ? classes.ctaAcid : classes.ctaOutline}`} disabled={busy} onClick={() => subscribe(p)}>
        Assinar
      </Button>
    );
  };

  return (
    <div className={classes.page}>
      <CheckoutModal
        plan={checkout}
        open={!!checkout}
        onClose={() => setCheckout(null)}
        onDone={() => { setCheckout(null); load(); }}
      />
      <div className={classes.wrap}>
        <div className={classes.head}>
          <Typography className={classes.title}>Escolha o plano da sua conta</Typography>
          <Typography className={classes.subtitle}>
            Assinatura mensal com limites de plataforma. O consumo (voz, IA, WhatsApp) é debitado à parte do saldo pré-pago.
          </Typography>
        </div>

        <div className={classes.grid}>
          {plans.map(p => {
            const isCurrent = p.code === current;
            const isBusiness = p.monthlyPriceCents == null;
            const rec = p.code === "pro"; // card escuro é o gesto de marca — sempre presente
            const cardCls = [classes.card, rec && classes.cardRec, isCurrent && !rec && classes.cardCurrent].filter(Boolean).join(" ");
            return (
              <div key={p.code} className={cardCls}>
                {rec && <img src={numeraMark} alt="" className={classes.mark} />}

                {isCurrent ? (
                  <span className={`${classes.badge} ${classes.badgeCurrent}`}>Plano atual</span>
                ) : rec ? (
                  <span className={`${classes.badge} ${classes.badgeRec}`}>Recomendado</span>
                ) : (
                  <div className={classes.badgeSpacer} />
                )}

                <div className={`${classes.name} ${rec ? classes.nameRec : ""}`}>{p.name}</div>
                <div className={`${classes.price} ${rec ? classes.priceRec : ""}`}>
                  {priceLabel(p.monthlyPriceCents)}
                  {showUnit(p.monthlyPriceCents) && <span className={`${classes.priceUnit} ${rec ? classes.priceUnitRec : ""}`}> /mês</span>}
                </div>
                <div className={`${classes.forWho} ${rec ? classes.forWhoRec : ""}`}>{AUDIENCE[p.code] || ""}</div>

                <div className={`${classes.divider} ${rec ? classes.dividerRec : ""}`} />

                <ul className={classes.feats}>
                  {featureRows(p.limits || {}).map(f => (
                    <li key={f.label} className={`${classes.feat} ${rec ? classes.featRec : ""}`}>
                      <CheckIcon />
                      <span><b>{f.val}</b> · {f.label}</span>
                    </li>
                  ))}
                </ul>

                {renderCta(p, isCurrent, isBusiness, rec)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Plans;
